import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import TimeEntryForm from './TimeEntryForm'
import TimezoneIndicator from './TimezoneIndicator'
import { UpdateTimeEntryInput } from '@/services/timeTracking'
import { Project, TimeEntry } from '../../../shared/types'
import { isLightColor, LIGHT_COLOR_CONTOUR_FILTER } from '@/utils/colorUtils'
import ConfirmationDialog from './ConfirmationDialog'

// Service TimeEntry interface (without isRunning property)
interface ServiceTimeEntry {
  id: string;
  userId: string;
  projectId?: string;
  description: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  tags?: string[];
  location?: string;
  timezone?: string; // IANA timezone identifier (e.g., "Europe/Berlin")
  createdAt: string;
  updatedAt: string;
}

// Helper function to convert service TimeEntry to shared TimeEntry
const convertToSharedTimeEntry = (entry: ServiceTimeEntry): TimeEntry => ({
  ...entry,
  isRunning: !entry.endTime
})

interface TimeEntriesListProps {
  timeEntries: ServiceTimeEntry[]
  runningEntry?: ServiceTimeEntry | null
  loading: boolean
  formatDuration: (seconds: number) => string
  getCurrentDuration: (entry: ServiceTimeEntry) => number
  deleteTimeEntry: (id: string) => Promise<void>
  updateTimeEntry: (id: string, updates: UpdateTimeEntryInput) => Promise<{ timeEntry: ServiceTimeEntry; message: string }>
  resumeTimeEntry?: (entry: ServiceTimeEntry, preferredTimezone?: string | null) => Promise<{ timeEntry: ServiceTimeEntry; message: string }>
  projects: Project[]
  showTimezoneIndicator?: boolean // From user display settings
  timezoneColorClass?: string // Custom color class for timezone indicator
  showOverlapIndicator?: boolean // From user display settings
  overlapIndicatorColor?: string // Custom color for the overlap warning icon (hex, default #ef4444)
  overlappingEntryIds?: Set<string> // Set of entry IDs that overlap with other entries
  showBudgetWarnings?: boolean // From user display settings
  budgetWarningColor?: string // Custom color for the budget violation icon (hex, default #ef4444)
  budgetViolatingEntryIds?: Set<string> // Set of entry IDs that violate budget
  preferredTimezone?: string | null // User's preferred timezone setting (null = use browser)
  weekStartsOn?: 0 | 1 // 0 = Sunday, 1 = Monday (default)
}

export default function TimeEntriesList({
  timeEntries,
  runningEntry,
  loading,
  formatDuration,
  getCurrentDuration,
  deleteTimeEntry,
  updateTimeEntry,
  resumeTimeEntry,
  projects,
  showTimezoneIndicator = true,
  timezoneColorClass = 'text-blue-600',
  showOverlapIndicator = true,
  overlapIndicatorColor,
  overlappingEntryIds = new Set<string>(),
  showBudgetWarnings = true,
  budgetWarningColor,
  budgetViolatingEntryIds = new Set<string>(),
  preferredTimezone,
  weekStartsOn = 1
}: TimeEntriesListProps) {
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean
    entryId: string | null
  }>({
    isOpen: false,
    entryId: null
  })

  const handleDelete = async (id: string) => {
    setDeleteDialog({
      isOpen: true,
      entryId: id
    })
  }

  const handleConfirmDelete = async () => {
    if (deleteDialog.entryId) {
      try {
        await deleteTimeEntry(deleteDialog.entryId)
      } catch {
        // Error is handled by the hook
      } finally {
        setDeleteDialog({ isOpen: false, entryId: null })
      }
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialog({ isOpen: false, entryId: null })
  }

  const handleEdit = (entry: ServiceTimeEntry) => {
    setEditingEntry(convertToSharedTimeEntry(entry))
  }

  const handleUpdate = async (updates: UpdateTimeEntryInput) => {
    if (editingEntry) {
      try {
        await updateTimeEntry(editingEntry.id, updates)
        setEditingEntry(null)
      } catch {
        // Error is handled by the hook
      }
    }
  }

  const handleResume = async (entry: ServiceTimeEntry) => {
    if (resumeTimeEntry) {
      try {
        await resumeTimeEntry(entry, preferredTimezone)
      } catch {
        // Error is handled by the hook
      }
    }
  }

  const handleEntryClick = (entry: ServiceTimeEntry) => {
    setEditingEntry(convertToSharedTimeEntry(entry))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }



  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Recent Time Entries</h2>
        </div>
        <div className="text-center py-8">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    )
  }

  // Combine running entry with regular entries, ensuring no duplicates
  const allEntries = [...timeEntries];
  if (runningEntry && !allEntries.find(entry => entry.id === runningEntry.id)) {
    allEntries.unshift(runningEntry); // Add running entry at the beginning
  }

  if (allEntries.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Recent Time Entries</h2>
        </div>
        <div className="text-center py-8">
          <div className="text-gray-500">No time entries yet</div>
          <p className="text-sm text-gray-400 mt-1">Start tracking your time to see entries here</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2 bg-green-100 rounded-lg">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Recent Time Entries</h2>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        {allEntries.slice(0, 6).map((entry) => {
          const project = projects.find(p => p.id === entry.projectId)
          const isRunning = !entry.endTime
          
          return (
            <div
              key={entry.id}
              className="rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleEntryClick(entry)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-gray-700 truncate pr-5">{entry.description}</h3>
                    {project && (
                      <div className="flex items-center space-x-2 mt-1 min-w-0">
                        <div
                          className="w-1 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: project.color }}
                        />
                        <span className="text-sm text-gray-500 truncate">{project.name}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {isRunning && (
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  )}
                  {project?.billable && (
                    <span title="Billable">
                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                        <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                  {isRunning && (
                    <span className="text-xs text-green-600">Running</span>
                  )}
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDuration(getCurrentDuration(entry))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center space-x-4">
                  {entry.location && (
                    <div className="flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" style={{ color: project?.color || '#6B7280' }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="truncate">{entry.location}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <span>{formatDate(entry.startTime)}</span>
                    <span>•</span>
                    <span>{formatTime(entry.startTime)}</span>
                    {entry.endTime && (
                      <>
                        <span>-</span>
                        <span>{formatTime(entry.endTime)}</span>
                      </>
                    )}
                    <TimezoneIndicator
                      entryTimezone={entry.timezone}
                      showIndicator={showTimezoneIndicator}
                      colorClass={timezoneColorClass}
                    />
                    {showOverlapIndicator && overlappingEntryIds.has(entry.id) && (
                      <span
                        className="inline-flex items-center justify-center w-4 h-4"
                        style={{
                          color: overlapIndicatorColor || "#ef4444",
                          filter: isLightColor(overlapIndicatorColor || "#ef4444") ? LIGHT_COLOR_CONTOUR_FILTER : undefined
                        }}
                        title="This entry overlaps with another entry in the same project"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                    {showBudgetWarnings && budgetViolatingEntryIds.has(entry.id) && (
                      <span
                        className="inline-flex items-center justify-center w-4 h-4"
                        style={{
                          color: budgetWarningColor || "#ef4444",
                          filter: isLightColor(budgetWarningColor || "#ef4444") ? LIGHT_COLOR_CONTOUR_FILTER : undefined
                        }}
                        title="This entry's day is out of budget"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-1">
                  {/* Project jump icon - only show if entry has a project */}
                  {entry.projectId && (
                    <Link
                      to={`/projects?project=${entry.projectId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-purple-600 focus:outline-none focus:text-purple-600"
                      title="View project"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h6a2 2 0 012 2v2H8V5z" />
                      </svg>
                    </Link>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEdit(entry)
                    }}
                    className="text-gray-400 hover:text-blue-600 focus:outline-none focus:text-blue-600"
                    title="Edit entry"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  {/* Resume button - only show for completed entries when no running entry */}
                  {entry.endTime && !runningEntry && resumeTimeEntry && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleResume(entry)
                      }}
                      className="text-gray-400 hover:text-green-600 focus:outline-none focus:text-green-600"
                      title="Resume entry"
                    >
                                                                                                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v14l11-7z" />
                                </svg>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(entry.id)
                    }}
                    className="text-gray-400 hover:text-red-600 focus:outline-none focus:text-red-600"
                    title="Delete entry"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {editingEntry && createPortal(
        <TimeEntryForm
          mode="edit"
          entry={editingEntry}
          projects={projects}
          onSubmit={handleUpdate}
          onCancel={() => setEditingEntry(null)}
          loading={loading}
          weekStartsOn={weekStartsOn}
        />,
        document.body
      )}

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Time Entry"
        message="Are you sure you want to delete this time entry?"
        confirmText="Delete"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={loading}
      />
    </div>
  )
} 